import { useAtomValue, useSetAtom } from "jotai";
import { type JSX, useRef } from "react";

import {
  LocationModal,
  type LocationModalHandle,
} from "@frontend/features/address-location";
import { useField } from "@frontend/hooks/form";
import { PickerField } from "@frontend/shared/picker-field";
import { ReferenceField } from "@frontend/shared/reference-field";
import { useLingui } from "@lingui/react/macro";

import {
  clientScopeAtom,
  optionsAtom,
  projectEditorAtom,
  projectForm,
  reloadPlacesAtom,
} from "../stores/editor";
import { FieldActionButton } from "./FieldActionButton";
import { Section } from "./Section";

const fields = projectForm.fields;

export function LocationSection(): JSX.Element {
  const { t } = useLingui();

  const scope = useAtomValue(clientScopeAtom);
  const options = useAtomValue(optionsAtom);
  const { type, project } = useAtomValue(projectEditorAtom);
  const reloadPlaces = useSetAtom(reloadPlacesAtom);

  const location = useField(fields.locationId);
  const deliveryLocation = useField(fields.deliveryLocationId);

  const modalRef = useRef<LocationModalHandle>(null);
  const askedBy = useRef<"location" | "deliveryLocation">("location");

  const intervention = "intervention" === type;

  const empty =
    null === scope.contactId
      ? t`Choose a client first.`
      : 0 === scope.locations.length
        ? t`This client has no place yet. Add one with the button beside.`
        : t`No match.`;

  const open = (
    field: "location" | "deliveryLocation",
    event: React.MouseEvent<HTMLElement>,
  ) => {
    askedBy.current = field;

    modalRef.current?.open(
      scope.contactId ?? undefined,
      event.currentTarget as HTMLElement,
    );
  };

  const newLocation = (id: number, field: "location" | "deliveryLocation") => {
    if ("location" === field) {
      location.setValue(id);
    } else {
      deliveryLocation.setValue(id);
    }
  };

  return (
    <Section icon="MapPin" legend={t`Locations`}>
      <PickerField
        label={t`Work location`}
        placeholder={t`Please choose an option`}
        items={scope.locations}
        field={fields.locationId}
        fallbackLabel={project?.locationLabel}
        empty={empty}
        popupWidth="content"
        actionPlacement="gutter"
        action={
          <FieldActionButton
            icon="MapPinPlus"
            label={t`New location`}
            disabled={null === scope.contactId}
            onClick={(event) => open("location", event)}
          />
        }
      />

      {intervention ? (
        <ReferenceField
          label={t`Starting site`}
          placeholder={t`Please choose an option`}
          items={options.sites}
          field={fields.intervention.fields.startingSiteId}
        />
      ) : (
        <PickerField
          label={t`Delivery location`}
          placeholder={t`Please choose an option`}
          items={scope.locations}
          field={fields.deliveryLocationId}
          fallbackLabel={project?.deliveryLocationLabel}
          empty={empty}
          popupWidth="content"
          actionPlacement="gutter"
          action={
            <FieldActionButton
              icon="MapPinPlus"
              label={t`New location`}
              disabled={null === scope.contactId}
              onClick={(event) => open("deliveryLocation", event)}
            />
          }
        />
      )}

      <LocationModal
        ref={modalRef}
        onSubmit={(created) => {
          void reloadPlaces().then(() =>
            newLocation(created.id, askedBy.current),
          );
        }}
      />
    </Section>
  );
}
